import { Component, Input, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Duration } from 'moment';
import moment from 'moment-timezone';
import { of, catchError, finalize, tap } from 'rxjs';

import { EventValues, PeriodValues } from '@shared/privacy/enums';
import { Privacy } from '@shared/account/models';
import { AuthUser } from '@shared/signup/models';
import { CommonRadioButtonItem } from '@tablesready/common-ui';
import { EVENTS, PERIODS } from '../../constants';
import { IDataRetention } from '../../models';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { AccountApiService } from '@tablesready/frontend-store';

@Component({
  selector: 'tablesready-organization-data-retention',
  templateUrl: './data-retention.component.html',
  styleUrls: ['./data-retention.component.scss'],
})
export class DataRetentionComponent implements OnChanges {
  @Input() user: AuthUser;

  form: UntypedFormGroup;
  countOfDays = 30;
  days: Array<{ name: string; value: number }> = [];
  privacyData: IDataRetention;
  submitting = false;
  periodRadioItems: CommonRadioButtonItem[] = [];
  eventsRadioItems: CommonRadioButtonItem[] = [];

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly accountApiService: AccountApiService,
    private readonly alertsService: AlertsService,
  ) { }

  setupDays(): void {
    this.days = [];
    for (let i = 0; i < this.countOfDays; i++) {
      this.days.push({
        name: String(i + 1),
        value: i + 1,
      });
    }
  }

  ngOnChanges(): void {
    this.periodRadioItems = PERIODS.map((period) => ({
      name: period.toUpperCase(),
      value: period,
    }))

    this.eventsRadioItems = EVENTS.map((eventItem) => ({
      name: eventItem.toUpperCase(),
      value: eventItem,
    }))
    this.privacyData = {
      event: EventValues.lastLogin,
      period: PeriodValues.months,
      number: 18,
    };

    this.privacyData = {
      event: this.user.account.privacy.event || EventValues.lastLogin,
      period: PeriodValues.months,
      number: 18,
    };
    if (this.user.account.privacy.time) {
      const time = moment.duration(this.user.account.privacy.time);
      this.convertDate(time);
    }

    this.form = this.fb.group({
      number: [this.privacyData.number, [Validators.required]],
      period: [this.privacyData.period, [Validators.required]],
      event: [this.privacyData.event, [Validators.required]]
    });

    this.setupDays();
  }

  convertDate(time: Duration): void {
    let month = 0;
    if (time.days() >= 30) {
      month += 1;
    }
    if (time.months()) {
      month += time.months();
    }
    if (time.years()) {
      month += 12 * time.years();
    }

    if (month) {
      this.privacyData.period = PeriodValues.months;
      this.privacyData.number = month;
    } else {
      this.privacyData.period = PeriodValues.days;
      this.privacyData.number = time.get('days');
    }
  }

  submit(): void {
    this.submitting = true;
    const value = this.form.value;
    const time = moment.duration(
        value.number || 1,
        value.period || PeriodValues.months
      ).asMilliseconds();
    const data: Privacy = {
      time,
      event: value.event,
    };

    this.accountApiService.updatePrivacySettings(data)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess('Privacy & Data');
          this.form.markAsPristine();
        }),
        catchError((error) => {
          this.alertsService.addError(`${error.error.error}`);

          return of(null);
        }),
        finalize(() => this.submitting = false),
      )
      .subscribe();
  }

  reset() {
    this.form.reset(this.privacyData);
  }
}
